<template>
  <!-- Conversation Details Header -->
  <div
    class="conversation-v2__details bg-neutral-50 border-b border-neutral-200 px-4 pt-5 pb-3 flex flex-col gap-3 shrink-0"
  >
    <!-- Row 1: Contact info + action buttons -->
    <div class="flex items-center gap-2">
      <!-- Contact info -->
      <div class="flex flex-1 gap-3 items-start min-w-0 overflow-hidden">
        <Thumbnail
          :src="currentContact.thumbnail"
          :badge="inboxBadge"
          :username="currentContact.name"
          :status="currentContact.availability_status"
          size="46px"
          class="shrink-0"
        />
        <div
          class="flex flex-col gap-1 items-start min-w-0 flex-1 justify-center"
        >
          <div class="flex items-center gap-2 w-full">
            <span
              class="text-xl font-bold text-gray-600 truncate leading-[30px]"
            >
              {{ currentContact.name }}
            </span>
            <span
              class="material-symbols-outlined text-[16px] text-gray-500 shrink-0"
            >
              info
            </span>
          </div>
          <div
            class="flex flex-row items-center gap-[5px] w-full overflow-hidden"
          >
            <inbox-name v-if="inbox" :inbox="inbox" :text-size="'text-xs'" />
            <woot-button
              class="p-0"
              size="small"
              variant="link"
              @click="$emit('contact-panel-toggle')"
            >
              {{ contactPanelToggleText }}
            </woot-button>
          </div>
        </div>
      </div>

      <!-- Button group -->
      <div class="flex items-center gap-2 shrink-0">
        <!-- Bot / Agent switcher -->
        <div class="flex h-[34px] rounded-full">
          <button
            class="flex items-center justify-end gap-1 min-w-[60px] px-2 ltr:rounded-l-full rtl:rounded-r-full border-solid border-t border-b ltr:border-l rtl:border-r transition-colors cursor-pointer outline-none"
            :class="{
              'border-green-400 bg-green-50 text-green-600 hover:bg-green-100': isBot,
              'border-gray-200 bg-white text-gray-400 hover:bg-gray-50': !isBot,
            }"
            @click="switchToBot"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18.75C9 18.3358 9.33579 18 9.75 18H14.25C14.6642 18 15 18.3358 15 18.75C15 19.1642 14.6642 19.5 14.25 19.5H9.75C9.33579 19.5 9 19.1642 9 18.75Z" fill="currentColor"/>
              <path d="M4.5 12.0923C4.5 10.1406 6.35206 8.64693 8.29529 8.82876C9.35662 8.92807 10.6064 9 12 9C13.3936 9 14.6434 8.92807 15.7047 8.82876C17.6479 8.64693 19.5 10.1406 19.5 12.0923V13.8288C19.5 14.5171 19.0312 15.1125 18.3531 15.2304C17.0855 15.4509 14.8419 15.75 12 15.75C9.1581 15.75 6.91446 15.4509 5.64687 15.2304C4.96875 15.1125 4.5 14.5171 4.5 13.8288V12.0923ZM11.3129 10.8525C11.1946 10.8324 11.0738 10.8701 10.988 10.9539L9.60643 12.3033C8.51581 12.2377 7.56916 12.1351 6.80094 12.0297C6.59575 12.0016 6.40661 12.1451 6.37848 12.3503C6.35034 12.5555 6.49388 12.7446 6.69906 12.7728C7.52357 12.8858 8.54696 12.9956 9.72905 13.0617C9.83409 13.0676 9.93677 13.0291 10.012 12.9556L11.143 11.8509L12.414 14.4165C12.4682 14.5259 12.5723 14.6021 12.693 14.6207C12.8138 14.6392 12.936 14.5978 13.0205 14.5097L14.4186 13.0532C15.5384 12.9864 16.5109 12.8811 17.3009 12.7728C17.5061 12.7446 17.6497 12.5555 17.6215 12.3503C17.5934 12.1451 17.4043 12.0016 17.1991 12.0297C16.3925 12.1403 15.3892 12.248 14.229 12.3129C14.1343 12.3182 14.0452 12.3592 13.9795 12.4276L12.8491 13.6053L11.586 11.0557C11.5328 10.9482 11.4312 10.8726 11.3129 10.8525Z" fill="currentColor"/>
              <path d="M12.75 2.79933C13.1984 2.53997 13.5 2.05521 13.5 1.5C13.5 0.671573 12.8284 0 12 0C11.1716 0 10.5 0.671573 10.5 1.5C10.5 2.05521 10.8016 2.53997 11.25 2.79933V4.5H8.25C4.52208 4.5 1.5 7.52208 1.5 11.25V12C0.671573 12 0 12.6716 0 13.5V16.5C0 17.3284 0.671573 18 1.5 18V19.5C1.5 21.1569 2.84315 22.5 4.5 22.5H19.5C21.1569 22.5 22.5 21.1569 22.5 19.5V18C23.3284 18 24 17.3284 24 16.5V13.5C24 12.6716 23.3284 12 22.5 12V11.25C22.5 7.52208 19.4779 4.5 15.75 4.5H12.75V2.79933ZM21 11.25V19.5C21 20.3284 20.3284 21 19.5 21H4.5C3.67157 21 3 20.3284 3 19.5V11.25C3 8.35051 5.35051 6 8.25 6H15.75C18.6495 6 21 8.35051 21 11.25Z" fill="currentColor"/>
            </svg>
            <span class="text-[10px] font-semibold">
              {{ $t('CONVERSATION.BOT') }}
            </span>
          </button>
          <button
            class="flex items-center gap-1 min-w-[60px] px-1.5 ltr:rounded-r-full rtl:rounded-l-full border border-solid transition-colors cursor-pointer outline-none"
            :class="{
              'border-green-400 bg-green-50 text-green-600 hover:bg-green-100': !isBot,
              'border-gray-200 bg-white text-gray-400 hover:bg-gray-50 ltr:border-l-green-400 rtl:border-r-green-400': isBot,
            }"
            @click="switchToAgent"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 1.5C7.85786 1.5 4.5 4.85786 4.5 9V10.5H6C6.82843 10.5 7.5 11.1716 7.5 12V16.5C7.5 17.3284 6.82843 18 6 18H4.5C3.67157 18 3 17.3284 3 16.5V9C3 4.02944 7.02944 0 12 0C16.9706 0 21 4.02944 21 9V18C21 20.0711 19.3211 21.75 17.25 21.75H14.0493C13.79 22.1984 13.3052 22.5 12.75 22.5H11.25C10.4216 22.5 9.75 21.8284 9.75 21C9.75 20.1716 10.4216 19.5 11.25 19.5H12.75C13.3052 19.5 13.79 19.8016 14.0493 20.25H17.25C18.4926 20.25 19.5 19.2426 19.5 18H18C17.1716 18 16.5 17.3284 16.5 16.5V12C16.5 11.1716 17.1716 10.5 18 10.5H19.5V9C19.5 4.85786 16.1421 1.5 12 1.5Z"
                fill="currentColor"
              />
            </svg>
            <span class="text-[10px] font-semibold">
              {{ $t('CONVERSATION.AGENT') }}
            </span>
          </button>
        </div>

        <!-- Snooze button -->
        <snooze-selector />

        <!-- Resolve button -->
        <resolve-action
          :conversation-id="currentChat.id"
          :status="currentChat.status"
        />
      </div>
    </div>

    <!-- Row 2: Action toolbar -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <!-- Agent dropdown -->
        <agent-selector :searchable="false" />

        <!-- Team dropdown -->
        <team-selector :searchable="true" />

        <!-- Priority dropdown -->
        <priority-selector />

        <!-- Labels / Tags -->
        <label-selector
          v-if="hasInboxPermission('Inbox assign labels')"
          :searchable="false"
        />

        <!-- Macros dropdown -->
        <macro-selector :conversation-id="currentChat.id" />
      </div>

      <!-- More actions -->
      <more-actions />
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import InboxName from 'dashboard/components/widgets/InboxName.vue';
import LabelSelector from './LabelSelector.vue';
import PrioritySelector from './PrioritySelector.vue';
import TeamSelector from './TeamSelector.vue';
import AgentSelector from './AgentSelector.vue';
import MacroSelector from './MacroSelector.vue';
import MoreActions from './MoreActions.vue';
import ResolveAction from 'dashboard/components/buttons/ResolveAction';
import SnoozeSelector from './SnoozeSelector';

import inboxMixin from 'shared/mixins/inboxMixin';
import agentMixin from 'dashboard/mixins/agentMixin.js';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import alertMixin from 'shared/mixins/alertMixin';
import wootConstants from 'dashboard/constants/globals';

export default {
  name: 'ConversationBoxV2',
  components: {
    Thumbnail,
    InboxName,
    LabelSelector,
    PrioritySelector,
    TeamSelector,
    AgentSelector,
    MacroSelector,
    MoreActions,
    SnoozeSelector,
    ResolveAction,
  },
  mixins: [inboxMixin, agentMixin, hasPermissionMixin, alertMixin],
  props: {
    isContactPanelOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      accountLabels: 'labels/getLabels',
    }),
    isBot() {
      const isOpen = this.currentChat.status === wootConstants.STATUS_TYPE.OPEN;
      const isResolved = this.currentChat.status === wootConstants.STATUS_TYPE.RESOLVED;

      return isOpen || isResolved;
    },
    currentContact() {
      const senderId = this.currentChat?.meta?.sender?.id;
      if (!senderId)
        return { name: '', thumbnail: '', availability_status: '' };
      return this.$store.getters['contacts/getContact'](senderId) || {};
    },
    inbox() {
      const inboxId = this.currentChat?.inbox_id;
      if (!inboxId) return {};
      return this.$store.getters['inboxes/getInbox'](inboxId) || {};
    },
    inboxBadge() {
      return this.inbox?.channel_type || '';
    },
    inboxInfo() {
      const phone = this.currentContact?.phone_number;
      const inboxName = this.inbox?.name;
      return phone || inboxName || '';
    },
    contactPanelToggleText() {
      return `${
        this.isContactPanelOpen
          ? this.$t('CONVERSATION.HEADER.CLOSE')
          : this.$t('CONVERSATION.HEADER.OPEN')
      } ${this.$t('CONVERSATION.HEADER.DETAILS')}`;
    },
  },
  methods: {
    toggleStatus(status, snoozedUntil) {
      this.isLoading = true;
      this.$store
        .dispatch('toggleStatus', {
          conversationId: this.currentChat.id,
          status,
          snoozedUntil,
        })
        .then(() => {
          this.showAlert(this.$t('CONVERSATION.CHANGE_STATUS'));
          this.isLoading = false;
        });
    },
    switchToBot() {
      this.toggleStatus(wootConstants.STATUS_TYPE.OPEN);
    },
    switchToAgent() {
      this.toggleStatus(wootConstants.STATUS_TYPE.PENDING);
    },
  },
};
</script>
